import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import { RiUploadLine } from '@remixicon/react';
import type { CheckImportResult } from '@internal/backstage-plugin-pulse-common';
import { useImportChecks } from '../../hooks';
import { isCheckImportBatchError } from '../../hooks/use-import-checks';
import { parseYamlDefinitions } from '../yaml-import';

type CheckImportDialogProps = {
  onImportStarted: () => void;
  onImported: (result: CheckImportResult) => void;
};

export const CheckImportDialog = ({
  onImportStarted,
  onImported,
}: CheckImportDialogProps) => {
  const {
    importChecks,
    loading,
    error: importError,
    clearError: clearImportError,
  } = useImportChecks();
  const [isOpen, setIsOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [definitions, setDefinitions] = useState<unknown[]>([]);
  const [parseError, setParseError] = useState<string>();

  const reset = (): void => {
    setFileName('');
    setDefinitions([]);
    setParseError(undefined);
    clearImportError();
  };

  const setOpen = (open: boolean): void => {
    setIsOpen(open);
    if (!open) {
      reset();
    }
  };

  const selectFile = async (file: File | undefined): Promise<void> => {
    reset();
    if (!file) {
      return;
    }

    setFileName(file.name);
    try {
      setDefinitions(parseYamlDefinitions(await file.text()));
    } catch (error) {
      setParseError(
        error instanceof Error ? error.message : 'Unable to read YAML file.',
      );
    }
  };

  const submit = async (): Promise<void> => {
    onImportStarted();
    try {
      const result = await importChecks(definitions);
      onImported(result);
      setOpen(false);
    } catch (error) {
      if (isCheckImportBatchError(error)) {
        onImported(error.partialResult);
      }
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        iconStart={<RiUploadLine />}
        onClick={() => setOpen(true)}
      >
        Import checks
      </Button>
      <Dialog isOpen={isOpen} onOpenChange={setOpen} width={560}>
        <DialogHeader>Import YAML check(s)</DialogHeader>
        <DialogBody>
          <Flex direction="column" gap="4">
            <Text>
              Select one file containing a single or multiple checks. Existing
              IDs will be skipped.
            </Text>
            <label htmlFor="pulse-check-import-file">
              <Text weight="bold">YAML file</Text>
            </label>
            <input
              id="pulse-check-import-file"
              type="file"
              accept=".yaml,.yml,text/yaml,application/yaml"
              disabled={loading}
              onChange={event => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                void selectFile(file);
              }}
            />
            {fileName && !parseError ? (
              <Text color="primary">
                {fileName}: {definitions.length}{' '}
                {definitions.length === 1 ? 'check' : 'checks'} found
              </Text>
            ) : null}
            {parseError ? (
              <Text color="danger" role="alert">
                {parseError}
              </Text>
            ) : null}
            {importError ? (
              <Text color="danger" role="alert">
                {importError.message}
              </Text>
            ) : null}
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Flex justify="end" gap="2">
            <Button
              variant="secondary"
              isDisabled={loading}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={loading}
              isDisabled={loading || definitions.length === 0}
              onClick={() => void submit()}
            >
              Import
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </>
  );
};
